import isEmpty from 'lodash/isEmpty.js';
import { useQuery } from 'react-query';
import useLocalStorage from 'react-use/lib/useLocalStorage.js';
import { addHours } from 'date-fns';
import { useSelector } from 'react-redux';

import useOrgId from 'hooks/useOrgId.js';
import useOrganizations from './useOrganizations.js';

import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useFirebase } from 'components/Firebase/index.js';
import { BILLING_PERMISSIONS } from 'shared-permissions';
import usePermissions from 'hooks/usePermissions.jsx';

const ONE_HOUR_TTL = 1;

const INITIAL_INVOICE_STORAGE_VALUE = {
  invoiceData: { shouldShowInvoiceOverdueBanner: false },
  invoiceTimestamp: 0,
};

const useBillingAlert = () => {
  const { canHaveBillingAlert } = useNavigationFrameContext();
  const { features } = useFeatureContext();
  const firebase = useFirebase();
  const { billingNotificationEnabled, deactivatedBillingNotificationEnabled } = features;
  const orgId = useOrgId();
  const { data: organizations } = useOrganizations();
  const currentOrganization = organizations.find(({ id }) => id === orgId);
  const isSuperadmin = useSelector((state) => state.sessionState.authUser?.isSuperadmin);
  const membership = useSelector((state) => state.sessionState.authUser?.membership?.(orgId));
  const { hasPermission } = usePermissions();
  const hasBillingPermissions =
    isSuperadmin ||
    Object.keys(BILLING_PERMISSIONS)
      .map((permission) => hasPermission(permission))
      .some((userHavePermission) => userHavePermission === true);
  const invoiceStorageKey = `invoice-storage-${orgId}`;
  const isDeactivated = Boolean(currentOrganization?.deactivated);

  const [invoiceStorage, setInvoiceStorage] = useLocalStorage(invoiceStorageKey, INITIAL_INVOICE_STORAGE_VALUE);
  const {
    invoiceData: { shouldShowInvoiceOverdueBanner },
    invoiceTimestamp,
  } = invoiceStorage;

  const isInvoiceStorageExpired = addHours(new Date(invoiceTimestamp), ONE_HOUR_TTL) < new Date();

  const shouldCheckForUnpaidInvoices =
    isInvoiceStorageExpired &&
    hasBillingPermissions &&
    !!orgId &&
    !!(isSuperadmin || membership) &&
    canHaveBillingAlert &&
    billingNotificationEnabled;

  useQuery({
    enabled: !!shouldCheckForUnpaidInvoices,
    retry: 3,
    queryKey: ['o', orgId, 'invoices', 'unpaid'],
    queryFn: async () => {
      const agent = await firebase.getAgent();
      const response = await agent.get(`/o/${orgId}/invoices/unpaid`);
      return response.body;
    },
    onSuccess: (invoiceData) => {
      if (isEmpty(invoiceData)) {
        setInvoiceStorage({
          ...INITIAL_INVOICE_STORAGE_VALUE,
          invoiceTimestamp: new Date(),
        });
        return;
      }

      setInvoiceStorage({ invoiceData, invoiceTimestamp: new Date() });
    },
    onError: (error) => {
      console.error('Failed to fetch unpaid invoices', error.message);

      setInvoiceStorage({
        ...INITIAL_INVOICE_STORAGE_VALUE,
        invoiceTimestamp: new Date(),
      });
    },
  });

  const isOverdue = shouldShowInvoiceOverdueBanner;
  const isOverdueAlertShown = canHaveBillingAlert && billingNotificationEnabled && isOverdue;
  const isDeactivatedAlertShown = canHaveBillingAlert && deactivatedBillingNotificationEnabled && isDeactivated;
  const isAlertShown = isOverdueAlertShown || isDeactivatedAlertShown;

  return {
    isAlertShown,
    isDeactivated,
  };
};

export default useBillingAlert;
